// LedgerCard — the card used by the My Robots grid view. Ported from
// botriq-admin-fe's Robot Portal UI kit (components/ui/LedgerCard.jsx),
// rewritten against this app's --bq- CSS custom properties.
//
// THE LEDGER LAYOUT (the point of this component)
// Key-value rows on a FIXED label column, so every value starts at the same x -
// down the card and, because LABEL_COL is a constant rather than content-derived,
// across every card in the grid row too. Label-above-value cells cannot do this:
// each label gets its own rendered height, so the values sit on different
// baselines and neighbouring cards read as ragged.
//
// Cards are uniform height BY CONSTRUCTION, not by stretching: callers pass the
// same number of rows for every card, nothing variable-height goes inside, and the
// card fills its grid cell with height:100%.
import PropTypes from "prop-types";
import { Card } from "./Card";

const LABEL_COL = "auto";
const GUTTER = 14;

// `cardRef` (not `ref`) - this is a plain function component, so a real ref would
// need forwardRef.
export const LedgerCard = ({ head, rows, onClick, tone, style, cardRef }) => (
  <Card
    ref={cardRef}
    onClick={onClick}
    style={{
      padding: 14,
      display: "flex",
      flexDirection: "column",
      gap: 12,
      height: "100%",
      ...(onClick && { cursor: "pointer" }),
      ...(tone === "danger" && {
        background: "var(--bq-dangerSoft)",
        borderColor: "var(--bq-danger)",
      }),
      ...style,
    }}
    className="rp-hover-lift"
  >
    {head}

    <div>
      {rows.map(({ key, label, icon, value, mono, dim }, i) => (
        <div
          key={key}
          data-testid={`ledger-row-${key}`}
          style={{
            display: "grid",
            gridTemplateColumns: `${LABEL_COL} minmax(0, 1fr)`,
            alignItems: "baseline",
            padding: i === 0 ? "0 0 6px" : "6px 0",
            ...(i > 0 && { borderTop: "1px solid var(--bq-border)" }),
          }}
        >
          <div
            data-testid={`ledger-label-${key}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              whiteSpace: "nowrap",
            }}
          >
            {icon}
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: ".04em",
                textTransform: "uppercase",
                color: "var(--bq-text3)",
              }}
            >
              {label}
            </span>
          </div>
          <div
            style={{
              minWidth: 0,
              textAlign: "right",
              paddingLeft: GUTTER,
              fontSize: 12.5,
              fontWeight: dim ? 500 : 600,
              color: dim ? "var(--bq-text3)" : "var(--bq-text)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              ...(mono && { fontFamily: "'JetBrains Mono', monospace" }),
            }}
          >
            {value}
          </div>
        </div>
      ))}
    </div>
  </Card>
);

LedgerCard.propTypes = {
  head: PropTypes.node.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      value: PropTypes.node,
      mono: PropTypes.bool,
      dim: PropTypes.bool,
    })
  ).isRequired,
  onClick: PropTypes.func,
  tone: PropTypes.oneOf(["danger"]),
  style: PropTypes.object,
  cardRef: PropTypes.object,
};

// Shared grid wrapper, so the grid gets the same column width and gutter as
// botriq-admin-fe's Fleet page.
export const LedgerGrid = ({ children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
      gap: 12,
    }}
  >
    {children}
  </div>
);

LedgerGrid.propTypes = { children: PropTypes.node };

export default LedgerCard;
